import { orderBy } from "lodash"
import {
  SearchFilterOption,
  SearchControllerParams,
  SearchFilterCategory,
  SearchFilters,
  SearchFilterPrices,
} from "../../../features/search/types"
import {
  FilterCategory,
  CheckboxFilterOption,
  CheckboxFilterType,
  RangeFilterOption,
  CheckboxFilterParentRef,
  FilterCategoryOptions,
} from "../components/filters/types"
import { AvailabilitySearchFilters } from "../types"

const isOptionSelected = (
  option: SearchFilterOption,
  searchParams: SearchControllerParams,
  initialFilters?: AvailabilitySearchFilters
) => {
  const key = option.value.split(":")[0]
  if (key === "brand") {
    if (
      (initialFilters?.brandCodes?.length ?? 0) > 0 &&
      initialFilters?.brandCodes?.length ===
        searchParams.filters.brandCodes?.length
    ) {
      return false
    }
    return (
      searchParams.filters.brandCodes?.some(
        (x: any) => x.toLowerCase() === option.code
      ) ?? false
    )
  }
  return (
    searchParams.filters.tags?.some(
      (x: any) => x.key === key && x.codes.includes(option.value.split(":")[1])
    ) ?? false
  )
}

const isOptionDisabled = (
  option: SearchFilterOption,
  searchParams: SearchControllerParams
) => {
  if (option.occurrences === 0) {
    return true
  }
  return searchParams.filters.categories?.some(
    (x: any) => option.code.toLowerCase() === x.toLowerCase()
  )
}

const optionType = (option: SearchFilterOption): CheckboxFilterType =>
  ["true", "false"].includes(option.code.toLowerCase()) ? "bool" : "string"

const toOption = (
  category: SearchFilterCategory,
  option: SearchFilterOption,
  searchParams: SearchControllerParams,
  initialFilters?: AvailabilitySearchFilters,
  parent?: CheckboxFilterParentRef
): CheckboxFilterOption => ({
  label: option.name,
  occurrences: option.occurrences,
  value: option.value,
  code: option.code,
  key: category.key,
  selected:
    !isOptionDisabled(option, searchParams) &&
    isOptionSelected(option, searchParams, initialFilters),
  type: optionType(option),
  children: option.children?.map((x) =>
    toOption(category, x, searchParams, initialFilters, {
      value: option.value,
    })
  ),
  parent,
  customAction: option.customAction,
  disabled: isOptionDisabled(option, searchParams),
})

const isCategoryMatching = (currentCategory: string, mainCategory: string) => {
  const compareLen = Math.min(currentCategory.length, mainCategory.length)
  const cat1 = currentCategory.substring(0, compareLen).toLowerCase()
  const cat2 = mainCategory.substring(0, compareLen).toLowerCase()
  return cat1 === cat2
}

const processCategoryOptions = (
  options: CheckboxFilterOption[],
  mainCategory: string
): CheckboxFilterOption[] => {
  const filteredOptions = options.filter((x) =>
    isCategoryMatching(x.code, mainCategory)
  )
  for (const option of filteredOptions) {
    if (option.children) {
      option.children = processCategoryOptions(option.children, mainCategory)
    }
  }
  return filteredOptions
}

const postProcessProductCategoryFilter = (
  searchParams: SearchControllerParams,
  category: FilterCategory
): FilterCategory => {
  if (searchParams.filters.categories?.length === 1) {
    category.options = processCategoryOptions(
      category.options as CheckboxFilterOption[],
      searchParams.filters.categories[0]
    )
  }
  return category
}

const toCategory = (
  category: SearchFilterCategory,
  searchParams: SearchControllerParams,
  initialFilters?: AvailabilitySearchFilters
): FilterCategory | undefined => {
  const categoryItem: FilterCategory = {
    id: category.key,
    key: category.key,
    name: category.categoryName,
    options: category.options.map((x) =>
      toOption(category, x, searchParams, initialFilters)
    ),
    type: "checkbox",
    weight: category.weight,
    active: category.active,
  }
  if (category.key === "category") {
    if ((initialFilters?.categories?.length ?? 0) > 1) {
      return undefined
    }
    return postProcessProductCategoryFilter(searchParams, categoryItem)
  }
  return categoryItem
}

const getCheckboxFilters = (
  filters: SearchFilters,
  searchParams: SearchControllerParams,
  initialFilters?: AvailabilitySearchFilters
): FilterCategory[] => {
  return filters.categories
    .map((x) => toCategory(x, searchParams, initialFilters))
    .filter((x) => x) as FilterCategory[]
}

const getPriceDiscountFilters = (
  filters: SearchFilters,
  searchParams: SearchControllerParams
): FilterCategory => {
  const showFilter = filters.discountPercent.values.length > 1
  return {
    id: "discounPercent",
    key: "discounPercent",
    name: "Sconto",
    type: "percent",
    options: showFilter
      ? orderBy(
          filters.discountPercent.values
            .filter((x) => (x.value ?? 0) > 0)
            .map((x) => ({
              key: "",
              code: x.value,
              label: `${x.value}%`,
              occurrences: x.occurrences,
              selected: searchParams.filters?.discountPercentValues?.includes(
                x.value
              ),
              value: x.value,
              values: [x.value],
            })),
          (x) => x.value,
          "desc"
        )
      : [],
    weight: 100000,
    active: true,
  }
}

const getPriceFilters = (prices: SearchFilterPrices): FilterCategory => ({
  id: "price",
  key: "price",
  name: "Prezzo",
  type: "range",
  options: [
    {
      min: 0,
      max: parseFloat(prices.max?.toFixed(2) ?? "0"),
      values: [0, parseFloat(prices.max?.toFixed(2) ?? "0")],
    } as RangeFilterOption,
  ],
  weight: 50,
  active: true,
})

export const convertToFilterCategories = (
  filters: SearchFilters,
  searchParams: SearchControllerParams,
  initialFilters?: AvailabilitySearchFilters
): FilterCategory[] =>
  orderBy(
    [
      ...[
        ...getCheckboxFilters(filters, searchParams, initialFilters),
        getPriceDiscountFilters(filters, searchParams),
      ].filter((x) => x.options.length > 0),
      getPriceFilters(filters.prices),
    ],
    (x) => x.weight,
    "desc"
  )

export const expandTreeOptions = (
  options: CheckboxFilterOption[]
): CheckboxFilterOption[] => {
  const values = []
  for (const option of options) {
    values.push(option)
    if (option.children) {
      values.push(...expandTreeOptions(option.children))
    }
  }
  return values
}

export const getDefaultExpandedOptions = (options: FilterCategoryOptions) =>
  expandTreeOptions(options as CheckboxFilterOption[]).filter(
    (x) => x.customAction === undefined
  )
